'use client';

import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { useEffect } from 'react';
import { useUserStore } from '../../hooks/useUser';
import { removeAllAds, shouldEnableAds, shouldShowAdsOnPage } from '../../utilities/ads';
import { isDev } from '../../utilities/env';

/**
 * Enhanced AdProvider component
 * - Conditionally loads AdSense script for non-Pro users
 * - Properly handles script loading with Next.js
 * - Removes ads when user becomes Pro during session
 */
export default function AdProvider({ children }) {
  const { isProMemberActive } = useUserStore();
  const pathname = usePathname();
  const adsenseId = process.env.NEXT_PUBLIC_ADSENSE_ID || 'ca-pub-3709556784139074';
  const showAds = shouldEnableAds(isProMemberActive) && shouldShowAdsOnPage(pathname);

  // Remove ads if user becomes pro member during session
  useEffect(() => {
    if (isProMemberActive) {
      removeAllAds();
    }
  }, [isProMemberActive]);

  return (
    <>
      {showAds && (
        <Script
          id='adsense-script'
          strategy='afterInteractive'
          crossOrigin='anonymous'
          src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adsenseId}${isDev ? '&adtest=on' : ''}`}
          data-adtest={isDev ? 'on' : undefined}
          onLoadSuccess={() => {
            // Only initialize page-level ads once
            if (typeof window !== 'undefined' && !window.autoAdsInitialized) {
              try {
                // Set the flag before pushing to prevent race conditions
                window.autoAdsInitialized = true;

                // Push the configuration
                (window.adsbygoogle = window.adsbygoogle || []).push({
                  google_ad_client: adsenseId,
                  enable_page_level_ads: true,
                  overlays: { bottom: false }, // Disable bottom anchor ads
                  ...(isDev ? { test_mode: true } : {}),
                });
              } catch (error) {
                console.error('AdSense initialization error:', error);
              }
            }
          }}
        />
      )}
      {children}
    </>
  );
}
